import type { NamedSet } from 'zustand/middleware';

import { cardDetailStrings } from '../../constants/strings';
import type { Settlement } from '../../queries/settlementQuery';
import type { User } from '../../queries/teamQuery';
import type { WalletData } from '../../queries/walletQuery';
import type { Approval } from '../../repositories/approvalRepository';
import type { ZustandStore } from '../store.type';

import type { CardSourceInfo, RoleMappedToTsId } from '.';

type AsideState = {
	aside: {
		walletCardDetailAside: {
			isOpen: boolean;
			cardDetail: WalletData | undefined;
		};
		viewReceiptAside: {
			isOpen: boolean;
			settlement: Settlement | undefined;
		};
		outOfOfficeAside: {
			isOpen: boolean;
		};
		requestAside: {
			isOpen: boolean;
		};
		addAdminAside: {
			isOpen: boolean;
		};
		addPeopleAside: {
			isOpen: boolean;
		};
		invitePeopleAside: {
			isOpen: boolean;
			// shouldOpenAddAdminAside is used to know if the add admin aside
			// should be open after the invite people aside is closed
			shouldOpenAddAdminAside: boolean;
		};
		teamAside: {
			isOpen: boolean;
		};
		selectCardSource: {
			isOpen: boolean;
		};
		// enroll card aside
		addCardSource: {
			isOpen: boolean;
			currentTeamName: string; // currentTeamName set on save
			currentCardId: CardSourceInfo['id']; // currentCardId set on save
		};
		cardSourceDetail: {
			isOpen: boolean;
			cardSourceId: string;
		};
		cantRemoveCard: {
			isOpen: boolean;
			message: string;
		};
		removeCard: {
			isOpen: boolean;
			message: string;
		};
		selectTeams: {
			isOpen: boolean;
			model: {
				id: string[];
			};
			options: unknown[];
			title: string;
		};
		userRole: {
			isOpen: boolean;
			curentUserRole: RoleMappedToTsId;
		};
		userDetails: {
			isOpen: boolean;
			user: User;
			isSaveDisabled: boolean;
			reqLimitError: string;
		};
		userApprover: {
			isOpen: boolean;
		};
		codingListNewEntryAside: {
			isOpen: boolean;
		};
		codingListSelectorAside: {
			isOpen: boolean;
			isNewEntryAsideOpen: boolean;
		};
		removeCodingEntryAside: {
			isOpen: boolean;
		};
		createCodingList: {
			isOpen: boolean;
		};
		customCodingList: {
			isOpen: boolean;
		};
		renameCustomCodingList: {
			isOpen: boolean;
		};
		removeCodingList: {
			isOpen: boolean;
		};
		createCardAside: {
			isOpen: boolean;
			approval: Approval | undefined;
		};
		downloadReportAside: {
			isOpen: boolean;
			filters: any;
		};
	};
};

type AsideActions = {
	setIsOutOfOfficeAsideOpen: (isOpen: AsideState['aside']['outOfOfficeAside']['isOpen']) => void;
	setIsTeamAsideOpen: (isOpen: AsideState['aside']['teamAside']['isOpen']) => void;
	setIsAddAdminAsideOpen: (isOpen: AsideState['aside']['addAdminAside']['isOpen']) => void;
	setIsAddPeopleAsideOpen: (isOpen: AsideState['aside']['addPeopleAside']['isOpen']) => void;
	setIsInvitePeopleAsideOpen: (isOpen: AsideState['aside']['invitePeopleAside']['isOpen']) => void;
	setShouldOpenAddAdminAsideAsideOpen: (
		isOpen: AsideState['aside']['invitePeopleAside']['shouldOpenAddAdminAside'],
	) => void;
	setIsSelectCardSourceOpen: (isOpen: AsideState['aside']['selectCardSource']['isOpen']) => void;
	setIsAddSourceCardOpen: (isOpen: AsideState['aside']['addCardSource']['isOpen']) => void;
	setCurrentTeamNameOnSubmit: (isOpen: AsideState['aside']['addCardSource']['currentTeamName']) => void;
	setCurrentCardIdOnSubmit: (isOpen: AsideState['aside']['addCardSource']['currentCardId']) => void;
	setIsCardSourceDetailOpen: (data: AsideState['aside']['cardSourceDetail']) => void;
	setIsSelectTeamsOpen: (data: Partial<AsideState['aside']['selectTeams']>) => void;
	setOpenAsides: ({ type, isOpen }) => void;
	setIsUserRoleAsideOpen: (isOpen: AsideState['aside']['userRole']['isOpen']) => void;
	setCurrentUserRoleAside: (isOpen: AsideState['aside']['userRole']['curentUserRole']) => void;
	setIsUserDetailsAsideOpen: (isOpen: AsideState['aside']['userDetails']['isOpen']) => void;
	setIsUserDetailSaveBtnDisabled: (isSaveDisabled: AsideState['aside']['userDetails']['isSaveDisabled']) => void;
	setRequestLimitError: (error: AsideState['aside']['userDetails']['reqLimitError']) => void;
	setCurrentUserDetailsAside: (isOpen: AsideState['aside']['userDetails']['user']) => void;
	setIsUserApproverAsideOpen: (isOpen: AsideState['aside']['userApprover']['isOpen']) => void;
	setIsCodingListNewEntryAsideOpen: (isOpen: AsideState['aside']['codingListNewEntryAside']['isOpen']) => void;
	setIsCodingListSelectorAsideOpen: (isOpen: AsideState['aside']['codingListSelectorAside']['isOpen']) => void;
	setIsNewEntryAsideOpen: (isOpen: AsideState['aside']['codingListSelectorAside']['isNewEntryAsideOpen']) => void;
	setIsRemoveCodingEntryAsideOpen: (isOpen: AsideState['aside']['removeCodingEntryAside']['isOpen']) => void;
	setIsCreateCodinglistAsideOpen: (isOpen: AsideState['aside']['createCodingList']['isOpen']) => void;
	setIsCustomCodingListAsideOpen: (isOpen: AsideState['aside']['customCodingList']['isOpen']) => void;
	setIsRenameCustomCodingListAsideOpen: (isOpen: AsideState['aside']['renameCustomCodingList']['isOpen']) => void;
	setIsRemoveCodingListAsideOpen: (isOpen: AsideState['aside']['removeCodingList']['isOpen']) => void;
	setIsRequestAsideOpen: (isOpen: AsideState['aside']['requestAside']['isOpen']) => void;
	setIsViewReceiptsAsideOpen: (isOpen: AsideState['aside']['viewReceiptAside']['isOpen']) => void;
	setViewReceiptsAsideSettlement: (isOpen: AsideState['aside']['viewReceiptAside']['settlement']) => void;
	setIsWalletCardDetailAsideOpen: (isOpen: AsideState['aside']['walletCardDetailAside']['isOpen']) => void;
	setWalletCardDetailAside: (isOpen: AsideState['aside']['walletCardDetailAside']['cardDetail']) => void;
	setIsCreateCardAsideOpen: (isOpen: AsideState['aside']['createCardAside']['isOpen']) => void;
	setCreateCardAsideApproval: (isOpen: AsideState['aside']['createCardAside']['approval']) => void;
	setIsDownloadReportAsideOpen: (isOpen: AsideState['aside']['downloadReportAside']['isOpen']) => void;
	setDownloadReportAsideFilters: (filters: AsideState['aside']['downloadReportAside']['filters']) => void;
};

export type AsideSlice = AsideState & AsideActions;

const asideInitialState: AsideState = {
	aside: {
		walletCardDetailAside: {
			isOpen: false,
			cardDetail: undefined,
		},
		viewReceiptAside: {
			isOpen: false,
			settlement: undefined,
		},
		outOfOfficeAside: {
			isOpen: false,
		},
		requestAside: {
			isOpen: false,
		},
		addPeopleAside: {
			isOpen: false,
		},
		invitePeopleAside: {
			isOpen: false,
			shouldOpenAddAdminAside: false,
		},
		addAdminAside: {
			isOpen: false,
		},
		teamAside: {
			isOpen: false,
		},
		selectCardSource: {
			isOpen: false,
		},
		addCardSource: {
			isOpen: false,
			currentTeamName: '',
			currentCardId: '',
		},
		cardSourceDetail: {
			isOpen: false,
			cardSourceId: '',
		},
		cantRemoveCard: {
			isOpen: false,
			message: cardDetailStrings.CANT_REMOVE_CARD,
		},
		removeCard: {
			isOpen: false,
			message: cardDetailStrings.REMOVE_CARD,
		},
		selectTeams: {
			isOpen: false,
			model: {
				id: [],
			},
			options: [],
			title: cardDetailStrings.SELECT_TEAMS,
		},
		userRole: {
			isOpen: false,
			curentUserRole: {} as RoleMappedToTsId,
		},
		userDetails: {
			isOpen: false,
			user: {} as User,
			isSaveDisabled: true,
			reqLimitError: '',
		},
		userApprover: {
			isOpen: false,
		},
		codingListNewEntryAside: {
			isOpen: false,
		},
		codingListSelectorAside: {
			/* settings/coding Select Teams Aside */
			isOpen: false,
			// extra prop to know if the aside is open for a new entry or for editing an existing one
			isNewEntryAsideOpen: false,
		},
		removeCodingEntryAside: {
			isOpen: false,
		},
		createCodingList: {
			isOpen: false,
		},
		customCodingList: {
			isOpen: false,
		},
		renameCustomCodingList: {
			isOpen: false,
		},
		removeCodingList: {
			isOpen: false,
		},
		createCardAside: {
			isOpen: false,
			approval: undefined,
		},
		downloadReportAside: {
			isOpen: false,
			filters: undefined,
		},
	},
};

export function asideSlice(set: NamedSet<ZustandStore>): AsideSlice {
	return {
		...asideInitialState,
		setIsOutOfOfficeAsideOpen(isOpen: AsideState['aside']['outOfOfficeAside']['isOpen']): void {
			set(
				(state: AsideState) => {
					state.aside.outOfOfficeAside.isOpen = isOpen;
					return state;
				},
				false,
				'[setIsOutOfOfficeAsideOpen]',
			);
		},
		setIsAddAdminAsideOpen(isOpen: AsideState['aside']['addAdminAside']['isOpen']): void {
			set(
				(state: AsideState) => {
					state.aside.addAdminAside.isOpen = isOpen;
					return state;
				},
				false,
				'[setIsAddAdminAsideOpen]',
			);
		},
		setIsAddPeopleAsideOpen(isOpen: AsideState['aside']['addPeopleAside']['isOpen']): void {
			set(
				(state: AsideState) => {
					state.aside.addPeopleAside.isOpen = isOpen;
					return state;
				},
				false,
				'[setIsAddPeopleAsideOpen]',
			);
		},
		setIsInvitePeopleAsideOpen(isOpen: AsideState['aside']['invitePeopleAside']['isOpen']): void {
			set(
				(state: AsideState) => {
					state.aside.invitePeopleAside.isOpen = isOpen;
					return state;
				},
				false,
				'[setIsInvitePeopleAsideOpen]',
			);
		},
		setShouldOpenAddAdminAsideAsideOpen(
			shouldOpenAddAdminAside: AsideState['aside']['invitePeopleAside']['shouldOpenAddAdminAside'],
		): void {
			set(
				(state: AsideState) => {
					state.aside.invitePeopleAside.shouldOpenAddAdminAside = shouldOpenAddAdminAside;
					return state;
				},
				false,
				'[setShouldOpenAddAdminAsideAsideOpen]',
			);
		},
		setIsTeamAsideOpen(isOpen: AsideState['aside']['teamAside']['isOpen']): void {
			set(
				(state: AsideState) => {
					state.aside.teamAside.isOpen = isOpen;
					return state;
				},
				false,
				'[setIsTeamAsideOpen]',
			);
		},
		setIsSelectCardSourceOpen(isOpen: AsideState['aside']['selectCardSource']['isOpen']): void {
			set(
				(state: AsideState) => {
					state.aside.selectCardSource.isOpen = isOpen;
					return state;
				},
				false,
				'[setIsSelectCardSourceOpen]',
			);
		},
		setIsAddSourceCardOpen(isOpen: AsideState['aside']['addCardSource']['isOpen']): void {
			set(
				(state: AsideState) => {
					state.aside.addCardSource.isOpen = isOpen;
					return state;
				},
				false,
				'[setIsAddSourceCardOpen]',
			);
		},
		setCurrentTeamNameOnSubmit(currentTeamName: AsideState['aside']['addCardSource']['currentTeamName']): void {
			set(
				(state: AsideState) => {
					state.aside.addCardSource.currentTeamName = currentTeamName;
					return state;
				},
				false,
				'[setCurrentTeamNameOnSubmit]',
			);
		},
		setCurrentCardIdOnSubmit(currentCardId: AsideState['aside']['addCardSource']['currentCardId']): void {
			set(
				(state: AsideState) => {
					state.aside.addCardSource.currentCardId = currentCardId;
					return state;
				},
				false,
				'[setCurrentCardIdOnSubmit]',
			);
		},
		setIsCardSourceDetailOpen(data: AsideState['aside']['cardSourceDetail']): void {
			set(
				(state: AsideState) => {
					state.aside.cardSourceDetail = {
						isOpen: data.isOpen,
						cardSourceId: data.cardSourceId || state.aside.cardSourceDetail.cardSourceId,
					};
					return state;
				},
				false,
				'[setCardSourceDetailOpen]',
			);
		},
		setIsSelectTeamsOpen(data: Partial<AsideState['aside']['selectTeams']>): void {
			set(
				(state: AsideState) => {
					state.aside.selectTeams = {
						isOpen: data.isOpen ?? state.aside.selectTeams.isOpen,
						model: data.model || state.aside.selectTeams.model,
						options: data.options || state.aside.selectTeams.options,
						title: data.title || state.aside.selectTeams.title,
					};
					return state;
				},
				false,
				'[setIsSelectTeamsOpen]',
			);
		},
		setOpenAsides({ type, isOpen }): void {
			set(
				(state: AsideState) => {
					state.aside[type] = {
						isOpen,
					};
					return state;
				},
				false,
				'[setIsSelectTeamsOpen]',
			);
		},
		setIsUserRoleAsideOpen(isOpen: AsideState['aside']['userRole']['isOpen']): void {
			set(
				(state: AsideState) => {
					state.aside.userRole.isOpen = isOpen;
					return state;
				},
				false,
				'[setIsUserRoleAsideOpen]',
			);
		},
		setCurrentUserRoleAside(role: AsideState['aside']['userRole']['curentUserRole']): void {
			set(
				(state: AsideState) => {
					state.aside.userRole.curentUserRole = role;
					return state;
				},
				false,
				'[setCurrentUserRoleAside]',
			);
		},
		setIsUserDetailsAsideOpen(isOpen: AsideState['aside']['userDetails']['isOpen']): void {
			set(
				(state: AsideState) => {
					state.aside.userDetails.isOpen = isOpen;
					return state;
				},
				false,
				'[setIsUserDetailsAsideOpen]',
			);
		},
		setIsUserDetailSaveBtnDisabled(isSaveDisabled: AsideState['aside']['userDetails']['isSaveDisabled']): void {
			set(
				(state: AsideState) => {
					state.aside.userDetails.isSaveDisabled = isSaveDisabled;
					return state;
				},
				false,
				'[setIsUserDetailSaveBtnDisabled]',
			);
		},
		setRequestLimitError(reqLimitError: AsideState['aside']['userDetails']['reqLimitError']): void {
			set(
				(state: AsideState) => {
					state.aside.userDetails.reqLimitError = reqLimitError;
					return state;
				},
				false,
				'[setRequestLimitError]',
			);
		},
		setCurrentUserDetailsAside(user: AsideState['aside']['userDetails']['user']): void {
			set(
				(state: AsideState) => {
					state.aside.userDetails.user = user;
					return state;
				},
				false,
				'[setCurrentUserDetailsAside]',
			);
		},
		setIsUserApproverAsideOpen(isOpen: AsideState['aside']['userApprover']['isOpen']): void {
			set(
				(state: AsideState) => {
					state.aside.userApprover.isOpen = isOpen;
					return state;
				},
				false,
				'[setIsUserApproverAsideOpen]',
			);
		},
		setIsCodingListNewEntryAsideOpen(isOpen: AsideState['aside']['codingListNewEntryAside']['isOpen']): void {
			set(
				(state: AsideState) => {
					state.aside.codingListNewEntryAside.isOpen = isOpen;
					return state;
				},
				false,
				'[setIsCodingListNewEntryAsideOpen]',
			);
		},
		setIsCodingListSelectorAsideOpen(isOpen: AsideState['aside']['codingListSelectorAside']['isOpen']): void {
			set(
				(state: AsideState) => {
					state.aside.codingListSelectorAside.isOpen = isOpen;
					return state;
				},
				false,
				'[setIsCodingListSelectorAsideOpen]',
			);
		},
		setIsNewEntryAsideOpen(
			isNewEntryAsideOpen: AsideState['aside']['codingListSelectorAside']['isNewEntryAsideOpen'],
		): void {
			set(
				(state: AsideState) => {
					state.aside.codingListSelectorAside.isNewEntryAsideOpen = isNewEntryAsideOpen;
					return state;
				},
				false,
				'[setIsNewEntryAsideOpen]',
			);
		},
		setIsRemoveCodingEntryAsideOpen(isOpen: AsideState['aside']['removeCodingEntryAside']['isOpen']): void {
			set(
				(state: AsideState) => {
					state.aside.removeCodingEntryAside.isOpen = isOpen;
					return state;
				},
				false,
				'[setIsRemoveCodingEntryAsideOpen]',
			);
		},
		setIsCreateCodinglistAsideOpen(isOpen: AsideState['aside']['createCodingList']['isOpen']): void {
			set(
				(state: AsideState) => {
					state.aside.createCodingList.isOpen = isOpen;
					return state;
				},
				false,
				'[setIsCreateCodinglistAsideOpen]',
			);
		},

		setIsCustomCodingListAsideOpen(isOpen: AsideState['aside']['customCodingList']['isOpen']): void {
			set(
				(state: AsideState) => {
					state.aside.customCodingList.isOpen = isOpen;
					return state;
				},
				false,
				'[setIsCustomCodingListAsideOpen]',
			);
		},
		setIsRenameCustomCodingListAsideOpen(isOpen: AsideState['aside']['renameCustomCodingList']['isOpen']): void {
			set(
				(state: AsideState) => {
					state.aside.renameCustomCodingList.isOpen = isOpen;
					return state;
				},
				false,
				'[setIsRenameCustomCodingListAsideOpen]',
			);
		},
		setIsRemoveCodingListAsideOpen(isOpen: AsideState['aside']['removeCodingList']['isOpen']): void {
			set(
				(state: AsideState) => {
					state.aside.removeCodingList.isOpen = isOpen;
					return state;
				},
				false,
				'[setIsRemoveCodingListAsideOpen]',
			);
		},
		setIsRequestAsideOpen(isOpen: AsideState['aside']['requestAside']['isOpen']): void {
			set(
				(state: AsideState) => {
					state.aside.requestAside.isOpen = isOpen;
					return state;
				},
				false,
				'[setIsRequestAsideOpen]',
			);
		},
		setIsViewReceiptsAsideOpen(isOpen: AsideState['aside']['viewReceiptAside']['isOpen']): void {
			set(
				(state: AsideState) => {
					state.aside.viewReceiptAside.isOpen = isOpen;
					return state;
				},
				false,
				'[setIsViewReceiptsAsideOpen]',
			);
		},
		setViewReceiptsAsideSettlement(settlement: Settlement | undefined): void {
			set(
				(state: AsideState) => {
					state.aside.viewReceiptAside.settlement = settlement;
					return state;
				},
				false,
				'[setViewReceiptsAsideSettlement]',
			);
		},
		setIsWalletCardDetailAsideOpen: (isOpen: AsideState['aside']['walletCardDetailAside']['isOpen']) => {
			set(
				(state: AsideState) => {
					state.aside.walletCardDetailAside.isOpen = isOpen;
					return state;
				},
				false,
				'[setIsWalletCardDetailAsideOpen]',
			);
		},
		setWalletCardDetailAside: (cardDetail: AsideState['aside']['walletCardDetailAside']['cardDetail']) => {
			set(
				(state: AsideState) => {
					state.aside.walletCardDetailAside.cardDetail = cardDetail;
					return state;
				},
				false,
				'[setWalletCardDetailAside]',
			);
		},
		setIsCreateCardAsideOpen: (isOpen: AsideState['aside']['createCardAside']['isOpen']) => {
			set(
				(state: AsideState) => {
					state.aside.createCardAside.isOpen = isOpen;
					return state;
				},
				false,
				'[setIsCreateCardAsideOpen]',
			);
		},
		setCreateCardAsideApproval: (approval: AsideState['aside']['createCardAside']['approval']) => {
			set(
				(state: AsideState) => {
					state.aside.createCardAside.approval = approval;
					return state;
				},
				false,
				'[setCreateCardAsideApproval]',
			);
		},
		setIsDownloadReportAsideOpen: (isOpen: AsideState['aside']['downloadReportAside']['isOpen']) => {
			set(
				(state: AsideState) => {
					state.aside.downloadReportAside.isOpen = isOpen;
					return state;
				},
				false,
				'[setIsDownloadReportAsideOpen]',
			);
		},
		setDownloadReportAsideFilters: (filters: AsideState['aside']['downloadReportAside']['filters']) => {
			set(
				(state: AsideState) => {
					state.aside.downloadReportAside.filters = filters;
					return state;
				},
				false,
				'[setDownloadReportAsideFilters]',
			);
		},
	};
}
